import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import LottieAnimation from "./LottieAnimation";

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: #f8f9fa;
  border-radius: 24px;
  padding: clamp(12px, 2.5vh, 25px) clamp(16px, 3vw, 30px);
  text-align: center;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StreakMessage = styled.div`
  font-size: clamp(14px, 2.2vh, 20px);
  color: #666;
  margin-bottom: clamp(8px, 1.5vh, 20px);
  font-family: "Quicksand", sans-serif;
  line-height: 1.4;
  padding: clamp(8px, 1.5vh, 15px);
  background: white;
  border-radius: 16px;
  border: 2px solid #e9ecef;
  width: 100%;
`;

const AnimationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: clamp(4px, 1vh, 20px) 0;
  height: clamp(80px, 18vh, 180px);
  
  > div {
    width: clamp(70px, 16vh, 160px);
    height: clamp(70px, 16vh, 160px);
  }
`;

const StreakNumber = styled(motion.div)`
  font-size: clamp(48px, 12vh, 120px);
  font-weight: bold;
  color: #ff9a00;
  margin: clamp(4px, 1vh, 15px) 0;
  font-family: "Quicksand", sans-serif;
  text-shadow: 0 4px 8px rgba(255, 154, 0, 0.3);
  line-height: 1;
`;

const StreakLabel = styled.div`
  font-size: clamp(18px, 3.5vh, 32px);
  font-weight: bold;
  color: #ff9a00;
  margin-bottom: clamp(4px, 1vh, 15px);
  font-family: "Quicksand", sans-serif;
`;

const WeekCalendar = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(4px, 1vw, 8px);
  margin: clamp(6px, 1.5vh, 20px) 0;
  padding: clamp(8px, 1.5vh, 15px);
  background: white;
  border-radius: 16px;
  width: 100%;
`;

const DayCircle = styled.div`
  width: clamp(28px, 5vh, 40px);
  height: clamp(28px, 5vh, 40px);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: clamp(9px, 1.3vh, 12px);
  font-weight: bold;
  color: ${props => props.completed ? '#fff' : '#999'};
  background: ${props => props.completed ? '#ff9a00' : '#e9ecef'};
  font-family: "Quicksand", sans-serif;
  
  ${props => props.today && `
    box-shadow: 0 0 0 3px rgba(255, 154, 0, 0.3);
  `}
`;

const ContinueButton = styled(motion.button)`
  padding: clamp(0.6rem, 1.5vh, 1rem) 2rem;
  border-radius: 16px;
  font-size: clamp(0.85rem, 1.8vh, 1rem);
  font-weight: bold;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  width: 360px;
  max-width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 0.01s ease;
  background: #8747d4;
  color: white;
  border: none;
  box-shadow: 0 4px 0 #6535a3;
  position: relative;
  top: 0;
  margin: clamp(8px, 1.5vh, 20px) auto 0 auto;

  &:hover {
    background: #9859e0;
  }

  &:active {
    top: 4px;
    box-shadow: 0 0 0 #6535a3;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IceOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;
`;

const IceSheet = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(235, 248, 255, 0.97), rgba(150, 210, 250, 0.95));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 72px;
`;

const IceShard = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70px;
  height: 70px;
  margin: -35px 0 0 -35px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(140, 205, 250, 0.9));
  clip-path: polygon(50% 0%, 100% 60%, 30% 100%, 0% 35%);
  box-shadow: inset 0 0 8px rgba(255, 255, 255, 0.9);
`;

const ReclaimedBadge = styled(motion.div)`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  color: #ffffff;
  background: linear-gradient(90deg, #ff9a00, #ff6b35);
  padding: 6px 14px;
  border-radius: 999px;
  box-shadow: 0 3px 0 #d4541f;
  margin-bottom: clamp(6px, 1.2vh, 12px);
  font-family: "Quicksand", sans-serif;
`;

const SHARDS = [
  { x: -170, y: -140, rotate: -120 },
  { x: 160, y: -160, rotate: 140 },
  { x: -190, y: 40, rotate: -60 },
  { x: 190, y: 60, rotate: 90 },
  { x: -110, y: 200, rotate: -160 },
  { x: 120, y: 210, rotate: 170 },
  { x: 0, y: -220, rotate: 45 },
];

const StreakModal = ({ isOpen, onClose, streakCount, restoredFrom = null }) => {
  const days = ["Sa", "Su", "Mo", "Tu", "We", "Th", "Fr"];
  const isRestored = restoredFrom !== null && restoredFrom !== undefined;
  const [displayCount, setDisplayCount] = useState(streakCount);

  // For a reclaimed streak, start from the frozen number and count up once the ice breaks
  useEffect(() => {
    if (!isOpen || !isRestored) {
      setDisplayCount(streakCount);
      return undefined;
    }
    setDisplayCount(restoredFrom);
    let current = restoredFrom;
    let intervalId;
    const startId = setTimeout(() => {
      intervalId = setInterval(() => {
        current += 1;
        setDisplayCount(current);
        if (current >= streakCount) clearInterval(intervalId);
      }, 250);
    }, 1100);
    return () => {
      clearTimeout(startId);
      clearInterval(intervalId);
    };
  }, [isOpen, isRestored, restoredFrom, streakCount]);
  
  // Dynamic message based on streak count
  const getStreakMessage = (count) => {
    if (count === 1) {
      return "A streak is born! Practice every day to help it grow.";
    } else if (count === 2) {
      return "Your streak is growing! Keep the momentum going.";
    } else if (count === 3) {
      return "Three days strong! You're building a great habit.";
    } else if (count >= 4 && count <= 6) {
      return "Amazing consistency! Your dedication is paying off.";
    } else if (count === 7) {
      return "One week streak! You're on fire! 🔥";
    } else if (count >= 8 && count <= 13) {
      return "You're unstoppable! Keep that streak alive.";
    } else if (count === 14) {
      return "Two weeks of dedication! You're a learning machine.";
    } else if (count >= 15 && count <= 20) {
      return "Your commitment is inspiring! Don't break the chain.";
    } else if (count === 21) {
      return "21 days! You've officially built a habit! 🎉";
    } else if (count >= 22 && count <= 29) {
      return "Your discipline is remarkable! Keep pushing forward.";
    } else if (count === 30) {
      return "30 days! A full month of dedication! Incredible! 🏆";
    } else if (count >= 31 && count <= 49) {
      return "You're a true scholar! Your consistency inspires others.";
    } else if (count >= 50 && count <= 99) {
      return "Legendary commitment! You're building something extraordinary.";
    } else if (count >= 100) {
      return "100+ days! You're absolutely unstoppable! Master level! 👑";
    }
    return "Keep that amazing streak going!";
  };
  
  // Create a simple calendar showing the current week with the streak
  const getDayStatus = (dayIndex) => {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Convert to our array format: ["Sa", "Su", "Mo", "Tu", "We", "Th", "Fr"]
    // Saturday (6) -> 0, Sunday (0) -> 1, Monday (1) -> 2, etc.
    const adjustedToday = today === 6 ? 0 : today + 1;
    
    if (dayIndex <= adjustedToday) {
      // Mark days as completed if they're in the current streak range
      return dayIndex >= Math.max(0, adjustedToday - streakCount + 1);
    }
    return false;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              transition: {
                type: "spring",
                stiffness: 300,
                damping: 20
              }
            }}
            exit={{ scale: 0.3, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isRestored && (
              <IceOverlay>
                <IceSheet
                  initial={{ opacity: 1, scale: 1 }}
                  animate={{ opacity: 0, scale: 1.08 }}
                  transition={{ delay: 0.7, duration: 0.25 }}
                >
                  <motion.span
                    animate={{ rotate: [0, -8, 8, -8, 8, 0], scale: [1, 1.1, 1.1, 1.2, 1.3, 0.2] }}
                    transition={{ duration: 0.75 }}
                  >
                    ❄️
                  </motion.span>
                </IceSheet>
                {SHARDS.map((shard, i) => (
                  <IceShard
                    key={i}
                    initial={{ x: 0, y: 0, rotate: 0, opacity: 0 }}
                    animate={{
                      x: shard.x,
                      y: shard.y,
                      rotate: shard.rotate,
                      opacity: [0, 1, 1, 0],
                    }}
                    transition={{ delay: 0.7, duration: 0.9, ease: "easeOut" }}
                  />
                ))}
              </IceOverlay>
            )}

            {isRestored && (
              <ReclaimedBadge
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 1, duration: 0.5 }}
              >
                STREAK RECLAIMED
              </ReclaimedBadge>
            )}

            <StreakMessage>
              {isRestored
                ? `You thawed your ${restoredFrom}-day streak! The fire is back. 🔥`
                : getStreakMessage(streakCount)}
            </StreakMessage>
            
            <AnimationContainer>
              <LottieAnimation 
                lottieFile="https://d10jmd9bhq0s4v.cloudfront.net/General/LottieFiles/streak-fire.json"
                loop={true}
              />
            </AnimationContainer>
            
            <StreakNumber
              key={isRestored ? displayCount : "streak"}
              initial={{ scale: isRestored ? 0.6 : 0 }}
              animate={{ 
                scale: [isRestored ? 0.6 : 0, 1.2, 1],
                transition: { delay: isRestored ? 0 : 0.3, duration: 0.6 }
              }}
            >
              {displayCount}
            </StreakNumber>
            
            <StreakLabel>day streak</StreakLabel>
            
            <WeekCalendar>
              {days.map((day, index) => (
                <DayCircle
                  key={day}
                  completed={getDayStatus(index)}
                  today={index === (new Date().getDay() === 6 ? 0 : new Date().getDay() + 1)}
                >
                  <div>{day}</div>
                  <div style={{ fontSize: '8px', marginTop: '2px' }}>
                    {getDayStatus(index) ? '✓' : ''}
                  </div>
                </DayCircle>
              ))}
            </WeekCalendar>
            
            <ContinueButton
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isRestored ? "LET'S KEEP IT BURNING" : "I'M COMMITTED"}
            </ContinueButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default StreakModal; 